import { STATUS_CODE } from '@kc-console/core';

import { getGuardLogger } from './getGuardLogger';

import type { GetServerSidePropsContext } from 'next';
import type { ServiceAppState } from '@kc-console/context';

type GuardLogParams = {
  context: GetServerSidePropsContext;
  guardName: string;
  state: Partial<ServiceAppState>;
  forced?: boolean;
};

const getOsMemory = () => {
  if (typeof window !== 'undefined') return null;
  if (!globalThis.process?.versions?.node) return null;

  try {
    // 브라우저 번들에서 node 내장 모듈을 제외하기 위해 런타임 로드

    const os = require('os') as typeof import('os');
    const totalMemory = os.totalmem();
    const freeMemory = os.freemem();

    return {
      totalMemory,
      freeMemory,
      usedMemory: totalMemory - freeMemory,
    };
  } catch {
    return null;
  }
};

const getPercents = (part: number, total: number) => {
  const fixedNumber = ((part / total) * 100).toFixed(2);
  return parseFloat(fixedNumber);
};

/**
 * state 정보를 기반으로 가드 로그를 출력하는 함수
 * @description
 * - 가드 실패 또는 리다이렉트 시에만 로그를 출력합니다.
 * - forced를 통해 강제 출력도 가능합니다.
 */
export const printGuardLog = ({ guardName, state, context, forced = false }: GuardLogParams) => {
  if (!guardName) return; // MEMO: 익명 함수인 경우 이름이 없으므로 로그 출력하지 않음 (combined 함수)

  const memory = getOsMemory();

  const logger = getGuardLogger({
    context,
    state: state,
    guardName,
    custom: memory
      ? {
          os: {
            freemem: memory.freeMemory,
            totalmem: memory.totalMemory,
            usedmem: memory.usedMemory,
            freemem_ratio: getPercents(memory.freeMemory, memory.totalMemory),
            usedmem_ratio: getPercents(memory.usedMemory, memory.totalMemory),
          },
        }
      : {},
  });

  if (state.status && state.status !== STATUS_CODE.OK) {
    logger.info('GUARD FAILED: ' + guardName);
    return;
  }

  if (state.redirect) {
    logger.info('GUARD REDIRECTED: ' + guardName);
    return;
  }

  if (forced) {
    logger.info('GUARD LOGGED: ' + guardName);
  }
};
